/* ============================================================
   Complete the Collection — complete-set.js

   Custom element: <complete-set-add-to-cart>

   Adds every "missing AND in-stock" item from the product's
   linked set collection to the cart in one /cart/add.js request,
   tags each line with property `_bundle: complete-set`, then
   redirects to /cart so the shopper can review the full order.

   Server-side Liquid renders each collection item as a
   .complete-set__item child element with data attributes:
     data-variant-id, data-master-id, data-price (cents),
     data-owned ("true"/"false"), data-available ("true"/"false").
   The element reads those attributes; no API calls in the browser
   to determine ownership.

   Metafields:
     Product:    custom.complete_set       (collection reference)
     Collection: custom.set_price          (money — bundle target price)
   ============================================================ */

if (!customElements.get('complete-set-add-to-cart')) {
  customElements.define(
    'complete-set-add-to-cart',
    class CompleteSetAddToCart extends HTMLElement {

      connectedCallback() {
        this.btn      = this.querySelector('.complete-set__btn');
        this.labelEl  = this.querySelector('.complete-set__label');
        this.spinner  = this.querySelector('.loading__spinner');

        this.items = Array.from(this.querySelectorAll('.complete-set__item')).map((el) => ({
          variantId: parseInt(el.dataset.variantId, 10),
          owned:     el.dataset.owned === 'true',
          available: el.dataset.available === 'true',
        }));

        this.itemsToAdd = this.items.filter((i) => !i.owned && i.available && i.variantId);

        // Nothing buyable: lock the button and explain why.
        if (!this.itemsToAdd.length) {
          this.btn.disabled = true;
          const ownsAll = this.items.every((i) => i.owned);
          if (this.labelEl) {
            this.labelEl.textContent = ownsAll
              ? this.dataset.labelOwnsAll || 'You own the complete collection'
              : this.dataset.labelAllOOS  || 'Missing items currently out of stock';
          }
          // Hide the price + separator since there's nothing to add.
          this.querySelector('.complete-set__sep')?.remove();
          this.querySelector('.complete-set__price')?.remove();
          return;
        }

        this.btn.addEventListener('click', this.handleClick.bind(this));
      }

      async handleClick() {
        if (!this.itemsToAdd.length) return;

        this.btn.classList.add('loading');
        this.btn.setAttribute('aria-disabled', 'true');
        this.spinner?.classList.remove('hidden');

        try {
          const response = await fetch(
            window.Shopify.routes.root + 'cart/add.js',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({
                items: this.itemsToAdd.map((i) => ({
                  id: i.variantId,
                  quantity: 1,
                  properties: { _bundle: 'complete-set' },
                })),
              }),
            }
          );

          if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            console.error('[CompleteSet] Cart error (HTTP ' + response.status + '):', err);
            return;
          }

          // Multi-item add — bypass the single-item cart notification entirely
          // and send the customer to /cart so they see the whole order.
          window.location = window.Shopify.routes.root + 'cart';
        } catch (err) {
          console.error('[CompleteSet] Add to cart failed:', err);
        } finally {
          this.btn.classList.remove('loading');
          this.btn.removeAttribute('aria-disabled');
          this.spinner?.classList.add('hidden');
        }
      }
    }
  );
}
